import React, { useMemo, useState } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { scoreBand } from "../../utils/score-band";
import {
  ALL_EXAMS,
  MS_PER_DAY,
  MIN_QUIZZES_FOR_TREND,
  TREND_SPAN_DAYS,
  WINDOW_DAYS,
  dominantExamType,
  filterByExamType,
  listExamTypes,
  toTrendPoints,
  trendDelta
} from "../../utils/performance-trend";
import "./performance-trend.scss";

// SVG geometry: fixed viewBox, responsive via CSS width. Kept hand-rolled —
// no chart dependency in this app, and a sparkline is ~50 lines of SVG.
const VIEW_W = 600;
const VIEW_H = 140;
const PAD = 10;

// score-band.js thresholds; tint fills use the band colors at low opacity so
// the house color language carries without a legend.
const BAND_STRONG = 75;
const BAND_DEVELOPING = 50;

const yFor = (pct) => PAD + ((100 - pct) / 100) * (VIEW_H - 2 * PAD);

// Y-axis reference marks (D6): a couple of tick labels give a visible score
// scale so a dot's height is readable without hovering. Rendered as an HTML
// overlay (not SVG <text>) so the label size stays constant while the chart
// scales responsively; positioned by percentage of the fixed 0-100 axis.
const Y_TICKS = [100, 75, 50, 0];
const topPctFor = (pct) => (yFor(pct) / VIEW_H) * 100;

// Dot area encodes quiz size so evidence weight is visible: a 40-question
// quiz reads bigger than a 5-question one, without a huge radius range.
const radiusFor = (total) => Math.min(6, 2.5 + Math.sqrt(total) / 3);

const deltaPhrase = (delta) => {
  if (delta > 0) return `up ${delta} pts vs the prior ${WINDOW_DAYS} days`;
  if (delta < 0) return `down ${Math.abs(delta)} pts vs the prior ${WINDOW_DAYS} days`;
  return `even with the prior ${WINDOW_DAYS} days`;
};

// examChoice/onExamChange: optional controlled mode so the page can share
// one exam selection between the trend and the readiness card (slice 2).
// Uncontrolled (no props) keeps slice-1 behavior byte-for-byte.
const PerformanceTrend = ({ quizzes, now = Date.now(), examChoice: examChoiceProp, onExamChange }) => {
  const history = useHistory();
  const allPoints = useMemo(() => toTrendPoints(quizzes), [quizzes]);
  const examTypes = useMemo(() => listExamTypes(allPoints), [allPoints]);
  const defaultExam = useMemo(
    () => dominantExamType(allPoints) || ALL_EXAMS,
    [allPoints]
  );
  const [examChoiceLocal, setExamChoiceLocal] = useState(null);
  const examChoice = examChoiceProp !== undefined ? examChoiceProp : examChoiceLocal;
  const examSelected = examChoice ?? defaultExam;
  const handleExamChange = (value) =>
    onExamChange ? onExamChange(value) : setExamChoiceLocal(value);

  // Rows not fetched yet: render nothing rather than flashing the
  // "complete 2+ quizzes" thin state at every page load.
  if (!Array.isArray(quizzes)) {
    return null;
  }

  // D3: both thin states get a Create-a-quiz CTA (mirrors the quiz-history
  // empty-state pattern) so the primary-dashboard trend block never dead-ends
  // in prose at the moment the funnel most needs a nudge.
  const createQuizCta = (
    <Button
      variant="contained"
      color="primary"
      className="trend-cta"
      data-testid="trend-create-quiz"
      onClick={() => history.push("/create-quiz")}
    >
      Create a quiz
    </Button>
  );

  if (allPoints.length < MIN_QUIZZES_FOR_TREND) {
    // Honest thin state — never a flat fake line (design §3a).
    return (
      <Box component="div" className="performance-trend">
        <Typography component="h6" variant="h6" className="trend-title">
          Score Trend
        </Typography>
        <Typography variant="body2" className="trend-thin-state">
          Complete {MIN_QUIZZES_FOR_TREND}+ quizzes to see your trend here.
        </Typography>
        {createQuizCta}
      </Box>
    );
  }

  const spanStart = now - TREND_SPAN_DAYS * MS_PER_DAY;
  const filtered = filterByExamType(allPoints, examSelected);
  const points = filtered.filter((point) => point.time > spanStart && point.time <= now);
  const { current, prior, delta } = trendDelta(filtered, now);

  // D7: anchor the x-axis to the honest window [spanStart, now] so recency and
  // cadence read truthfully — six quizzes from last week cluster to the right
  // instead of stretching edge-to-edge as if they spanned the full 90 days.
  const domainSpan = Math.max(now - spanStart, 1);
  const xFor = (time) => PAD + ((time - spanStart) / domainSpan) * (VIEW_W - 2 * PAD);
  const linePath = points
    .map((point, i) => `${i === 0 ? "M" : "L"}${xFor(point.time).toFixed(1)},${yFor(point.pct).toFixed(1)}`)
    .join(" ");

  // D6: value label on the most-recent (rightmost) dot — the one number a
  // student most wants at a glance, readable on touch where <title> never fires.
  const latestPoint = points[points.length - 1];

  return (
    <Box component="div" className="performance-trend">
      <Box component="div" className="trend-header">
        <Typography component="h6" variant="h6" className="trend-title">
          Score Trend
          <Box component="span" className="trend-span-note">
            last {TREND_SPAN_DAYS} days
          </Box>
        </Typography>
        {examTypes.length > 0 && (
          <label className="trend-exam-filter">
            Exam:
            <select
              data-testid="trend-exam-select"
              value={examSelected}
              onChange={(event) => handleExamChange(event.target.value)}
            >
              {examTypes.map((examType) => (
                <option value={examType} key={examType}>
                  {examType}
                </option>
              ))}
              <option value={ALL_EXAMS}>All exams (mixed)</option>
            </select>
          </label>
        )}
      </Box>

      {points.length === 0 ? (
        <>
          <Typography variant="body2" className="trend-thin-state">
            {/* #520 gate note 4: this student HAS quizzes — just none recent
                (or none for this exam filter). Don't tell them to "start". */}
            No quizzes in the last {TREND_SPAN_DAYS} days
            {examSelected !== ALL_EXAMS ? ` for ${examSelected}` : ""} — your
            trend restarts with your next quiz.
          </Typography>
          {createQuizCta}
        </>
      ) : (
        <Box component="div" className="trend-chart">
          <svg
            className="trend-sparkline"
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={`Score trend: ${points.length} quizzes over the last ${TREND_SPAN_DAYS} days`}
          >
            <rect x="0" y={yFor(100)} width={VIEW_W} height={yFor(BAND_STRONG) - yFor(100)} className="band band-strong" />
            <rect x="0" y={yFor(BAND_STRONG)} width={VIEW_W} height={yFor(BAND_DEVELOPING) - yFor(BAND_STRONG)} className="band band-developing" />
            <rect x="0" y={yFor(BAND_DEVELOPING)} width={VIEW_W} height={yFor(0) - yFor(BAND_DEVELOPING)} className="band band-needs-work" />
            {/* D6: faint gridlines at the 75/50 band edges — a visible score
                reference the tint alone never provided. */}
            <line x1="0" x2={VIEW_W} y1={yFor(BAND_STRONG)} y2={yFor(BAND_STRONG)} className="trend-gridline" />
            <line x1="0" x2={VIEW_W} y1={yFor(BAND_DEVELOPING)} y2={yFor(BAND_DEVELOPING)} className="trend-gridline" />
            {points.length > 1 && <path d={linePath} className="trend-line" />}
            {points.map((point) => (
              <circle
                key={`${point.time}-${point.title}`}
                cx={xFor(point.time)}
                cy={yFor(point.pct)}
                r={radiusFor(point.total)}
                className={`trend-dot trend-dot--${scoreBand(point.pct)}`}
              >
                <title>
                  {`${point.title || "Quiz"} — ${point.pct}% of ${point.total} questions, ${moment(point.time).format("ll")}`}
                </title>
              </circle>
            ))}
          </svg>
          {/* D6: HTML overlays keep a constant, legible size while the SVG
              scales; positioned by percentage of the fixed 0-100 axis. */}
          <Box component="div" className="trend-yaxis" aria-hidden="true" data-testid="trend-yaxis">
            {Y_TICKS.map((tick) => (
              <Box
                component="span"
                key={tick}
                className="trend-ytick"
                style={{ top: `${topPctFor(tick)}%` }}
              >
                {tick}
              </Box>
            ))}
          </Box>
          {latestPoint && (
            <Box
              component="span"
              className={`trend-value-label trend-value-label--${scoreBand(latestPoint.pct)}`}
              data-testid="trend-value-label"
              style={{ top: `${topPctFor(latestPoint.pct)}%` }}
            >
              {latestPoint.pct}%
            </Box>
          )}
        </Box>
      )}

      <Typography variant="body2" className="trend-delta" data-testid="trend-delta">
        {current.accuracy !== null ? (
          <>
            Last {WINDOW_DAYS} days: <strong>{current.accuracy}%</strong> across{" "}
            {current.questions} questions
            {delta !== null && ` — ${deltaPhrase(delta)}`}
            {delta === null && prior.accuracy === null && " — no prior-period data yet"}
            .
          </>
        ) : (
          <>No quizzes in the last {WINDOW_DAYS} days.</>
        )}
      </Typography>
    </Box>
  );
};

export default PerformanceTrend;
